import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FolderOpen, Calculator as CalcIcon, PenLine, X, Minus, Square, LogOut, Folder, FileText, Image as ImageIcon, FolderPlus, ClipboardPaste, Copy, Scissors, Pencil, Trash2, RefreshCw, Users, NotebookPen, StickyNote, Landmark, FileType2, Table2, ArrowLeftRight, Usb, Monitor, Bug as BugIcon, Download, History, ScanText, Sparkles } from "lucide-react";
import { restorePrevious } from "./restorePrevious";
import { formatErr } from "@/lib/api";
import { dapi as api } from "./deskApi";
import { toast } from "sonner";
import FileManagerApp from "./FileManagerApp";
import CalculatorApp from "./CalculatorApp";
import SignDocsApp from "./SignDocsApp";
import LoginsApp from "./LoginsApp";
import NotepadApp from "./NotepadApp";
import ArtCraftApp, { ARTCRAFT_APPS, MAX_ARTCRAFT_WINDOWS, warmArtCraft } from "./ArtCraftApp";
import { ArtCraftUpdateNotice } from "./ArtCraftUpdateNotice";
import MoneyCalcApp from "./MoneyCalcApp";
import DocsApp from "./DocsApp";
import SheetsApp from "./SheetsApp";
import StickyNotes from "./StickyNotes";
import RecycleBinApp from "./RecycleBinApp";
import ConverterApp from "./ConverterApp";
import UsbApp from "./UsbApp";
import RemoteDesktopApp from "./RemoteDesktopApp";
import FeedbackAdmin from "../FeedbackAdmin";
import { fsClipboard, clipSet, clipClear, clipHas, DESKTOP_ROOT } from "./clipboard";
import { fileIcon } from "./fileIcon";
import { nextZ } from "./deskZ";
import { OpenWithDialog, OpenWithItems } from "./OpenWithMenu";
import SnipTool from "./SnipTool";
import AiUsageApp from "./AiUsageApp";

const onWindows = () => /Windows/i.test(navigator.userAgentData?.platform || navigator.userAgent || "");
// Opens Microsoft's own Snipping Tool (Windows 10/11). Chrome may ask "Open Snipping Tool?" the first time.
const openWindowsSnip = (fallback) => {
  const a = document.createElement("a");
  a.href = "ms-screenclip:";
  a.click();
  toast.message("Opening Microsoft Snipping Tool — tip: use its Text actions button to copy words.", {
    id: "ms-snip", duration: 6000, action: { label: "Use desktop snip", onClick: fallback },
  });
};
import { openWithDefault } from "./openWith";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/contexts/AuthContext";
import InstallAppButton from "@/components/InstallAppButton";
import { useLongPressMenu } from "@/lib/useLongPressMenu";

const APPS = {
  files: { title: "File Manager", icon: FolderOpen, w: 760, h: 480, color: "text-amber-500" },
  notepad: { title: "Notepad", icon: NotebookPen, w: 680, h: 480, color: "text-rose-500" },
  calc: { title: "Calculator", icon: CalcIcon, w: 320, h: 480, color: "text-sky-600" },
  sign: { title: "Signatures", icon: PenLine, w: 640, h: 480, color: "text-emerald-600" },
  money: { title: "Money Calc", icon: Landmark, w: 800, h: 560, color: "text-emerald-700" },
  word: { title: "Word", icon: FileType2, w: 1000, h: 680, color: "text-blue-600" },
  sheets: { title: "Excel", icon: Table2, w: 1100, h: 680, color: "text-green-600" },
  convert: { title: "Converter", icon: ArrowLeftRight, w: 760, h: 560, color: "text-fuchsia-400" },
  usb: { title: "USB Drive", icon: Usb, w: 780, h: 540, color: "text-sky-300" },
  remote: { title: "Remote Computers", icon: Monitor, w: 900, h: 600, color: "text-indigo-500" },
  recycle: { title: "Recycle Bin", icon: Trash2, w: 720, h: 480, color: "text-slate-400" },
  public: { title: "Public", icon: Folder, w: 760, h: 480, color: "text-sky-500" },
  ...ARTCRAFT_APPS,
};
const ADMIN_APPS = {
  ...APPS,
  logins: { title: "Desktop logins", icon: Users, w: 640, h: 440, color: "text-violet-500" },
  issues: { title: "Issue Tracker", icon: BugIcon, w: 940, h: 620, color: "text-amber-400" },
  aiusage: { title: "AI Usage", icon: Sparkles, w: 900, h: 620, color: "text-violet-500" },
};
// Only these stay as desktop icons; the rest are launched from the Start menu.
const DESKTOP_PINNED = ["files", "notepad", "calc", "sign", "recycle", "public"];

function Clock() {
  const [now, setNow] = useState(new Date());
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  return (
    <div ref={boxRef}>
      {open && (
        <div className="absolute bottom-14 right-2 z-[3002] rounded-lg bg-slate-900/95 backdrop-blur-md border border-white/10 shadow-2xl p-3" data-testid="taskbar-calendar">
            <div className="text-white text-sm font-semibold px-2 pb-2 border-b border-white/10">
              {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </div>
            <Calendar
              mode="single"
              selected={now}
              defaultMonth={now}
              className="text-white [&_button]:text-white [&_.rdp-day_button:hover]:bg-white/15"
              classNames={{
                day_selected: "bg-sky-500 text-white hover:bg-sky-500 focus:bg-sky-500",
                day_today: "bg-white/15 text-sky-300 font-bold",
              }}
            />
          </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-right text-xs text-white/90 leading-tight px-3 py-1 rounded hover:bg-white/15 ${open ? "bg-white/15" : ""}`}
        title="Open calendar"
        data-testid="desktop-clock"
      >
        <div>{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
        <div>{now.toLocaleDateString()}</div>
      </button>
    </div>
  );
}

const nodeIcon = (n) => fileIcon(n).Icon;

/** Windows-11-flavored mini desktop: full-screen takeover. Root files/folders
 * live on the desktop like a real PC — right-click for new folder, copy,
 * cut, paste, rename, delete; keyboard shortcuts (Ctrl+C/X/V, Del, F2). */
export default function DesktopTab({ onExit = null, deskUser = null }) {
  const { user: portalUser } = useAuth();
  // Only the REAL admin can hand out desktop logins — mgmt-portal logins
  // get the desktop without the "Desktop logins" app.
  const isRealAdmin = !deskUser && portalUser?.role === "admin";
  // Owner-only: the Issue Tracker collects bug reports for the developer.
  // Extra admins the owner invites can't act on them, so they don't get
  // the app.
  const isOwner = isRealAdmin && portalUser?.is_owner === true;
  const apps = useMemo(() => {
    if (deskUser || !isRealAdmin) return APPS;
    if (!isOwner) {
      const { issues: _omit, aiusage: _owner, ...rest } = ADMIN_APPS;
      return rest;
    }
    return ADMIN_APPS;
  }, [deskUser, isRealAdmin, isOwner]);
  // Phones/small tablets: windows always fill the screen so nothing is ever
  // positioned or sized off-screen (dragging/desktop coords don't apply).
  const [smallScreen, setSmallScreen] = useState(() => window.matchMedia("(max-width: 700px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 700px)");
    const h = (e) => setSmallScreen(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const [wins, setWins] = useState(() => {
    // Restore whatever windows were open before the refresh.
    try {
      const v = JSON.parse(localStorage.getItem("mgmt_open_wins"));
      // ArtCraft windows aren't reopened after a refresh: reopening many at once exhausts browser graphics.
      if (Array.isArray(v)) return v.filter((w) => w.app !== "pdf" && !ARTCRAFT_APPS[w.app]).map((w, i) => ({ ...w, z: nextZ(), pk: i + 1, min: !!w.min }));
    } catch { /* corrupt/absent */ }
    return [];
  }); // {app, x, y, z, min, props, pk}
  const [startOpen, setStartOpen] = useState(false);
  const [startQuery, setStartQuery] = useState("");
  const loadPins = (key, fallback) => {
    try { const v = JSON.parse(localStorage.getItem(key)); return Array.isArray(v) ? v : fallback; } catch { return fallback; }
  };
  const [deskPins, setDeskPins] = useState(() => loadPins("mgmt_desk_pins", DESKTOP_PINNED));
  const [taskPins, setTaskPins] = useState(() => loadPins("mgmt_task_pins", ["word", "sheets"]));

  // Pins live on the SERVER (per user) so they survive publishes, cache
  // clears and switching browsers. localStorage is just a fast first paint.
  const prefsLoaded = useRef(false);
  useEffect(() => {
    let alive = true;
    api.get("/desktop/prefs").then((r) => {
      if (!alive) return;
      const p = r.data || {};
      let dp = Array.isArray(p.deskPins) ? p.deskPins : DESKTOP_PINNED;
      // One-time: pin the new Public app to the desktop dock by default.
      if (!localStorage.getItem("mgmt_public_pin_v1")) {
        if (!dp.includes("public")) dp = [...dp, "public"];
        localStorage.setItem("mgmt_public_pin_v1", "1");
      }
      // ArtCraft programs live in the Start menu by default; undo the earlier auto-pin once.
      if (!p.artcraftStartOnly) {
        dp = dp.filter((k) => k !== "pdf" && !ARTCRAFT_APPS[k]);
      }
      setDeskPins(dp);
      if (Array.isArray(p.taskPins)) setTaskPins(p.taskPins);
    }).catch(() => {}).finally(() => { if (alive) prefsLoaded.current = true; });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!prefsLoaded.current) return; // don't overwrite server with defaults before load
    localStorage.setItem("mgmt_desk_pins", JSON.stringify(deskPins));
    localStorage.setItem("mgmt_task_pins", JSON.stringify(taskPins));
    api.put("/desktop/prefs", { prefs: { deskPins, taskPins, artcraftStartOnly: true } }).catch(() => {});
  }, [deskPins, taskPins]);


  const toggleDeskPin = (k, title) => setDeskPins((prev) => {
    const next = prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k];
    localStorage.setItem("mgmt_desk_pins", JSON.stringify(next));
    toast.success(prev.includes(k) ? `"${title}" removed from the desktop.` : `"${title}" pinned to the desktop.`);
    return next;
  });
  const pinDesk = (k, title) => setDeskPins((prev) => {
    if (prev.includes(k)) return prev;
    const next = [...prev, k];
    localStorage.setItem("mgmt_desk_pins", JSON.stringify(next));
    toast.success(`"${title}" pinned to the desktop.`);
    return next;
  });
  const pinTask = (k, title) => setTaskPins((prev) => {
    if (prev.includes(k)) return prev;
    const next = [...prev, k];
    localStorage.setItem("mgmt_task_pins", JSON.stringify(next));
    toast.success(`"${title}" pinned to the taskbar.`);
    return next;
  });
  const unpinTask = (k, title) => setTaskPins((prev) => {
    if (!prev.includes(k)) return prev;
    const next = prev.filter((x) => x !== k);
    localStorage.setItem("mgmt_task_pins", JSON.stringify(next));
    toast.success(`"${title}" unpinned from the taskbar.`);
    return next;
  });
  const [nodes, setNodes] = useState([]); // root fs items shown on the desktop
  const [selIds, setSelIds] = useState(() => new Set());
  const [marquee, setMarquee] = useState(null); // {x0,y0,x1,y1}
  const marqueeRef = useRef(null);
  const suppressClearRef = useRef(false);
  const [menu, setMenu] = useState(null); // {x, y, node?}
  const [openWith, setOpenWith] = useState(null);
  const [snip, setSnip] = useState(false);
  useEffect(() => {
    const key = (e) => { if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "s") { e.preventDefault(); setSnip(true); } };
    window.addEventListener("keydown", key);
    const ext = () => setSnip(true);
    window.addEventListener("desk-snip", ext);
    return () => { window.removeEventListener("keydown", key); window.removeEventListener("desk-snip", ext); };
  }, []);
  const [dragPos, setDragPos] = useState(null); // {id, x, y} while dragging an icon
  const [wbOpen, setWbOpen] = useState(null); // {title} while a signing workbench is open
  const pkRef = useRef(0);
  // pk must stay ahead of any restored windows' keys.
  useEffect(() => { pkRef.current = Math.max(pkRef.current, ...wins.map((w) => w.pk || 0), 0); }, [wins]);
  // Persist open windows (app + position + minimized/maximized + props) so a
  // refresh puts you right back where you were.
  useEffect(() => {
    try {
      localStorage.setItem("mgmt_open_wins", JSON.stringify(wins.map(({ app, x, y, min, maxed, props }) => ({ app, x, y, min, maxed, props }))));
    } catch { /* quota/private mode */ }
  }, [wins]);
  const dragRef = useRef(null);
  const iconDragRef = useRef(null);
  const nodesRef = useRef([]);
  const stickyRef = useRef(null);

  const loadNodes = useCallback(() => {
    api.get("/desktop/fs", { params: { parent_id: DESKTOP_ROOT } })
      .then((r) => setNodes(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { loadNodes(); }, [loadNodes]);
  useEffect(() => { warmArtCraft(); }, []);
  // refresh desktop icons when any app saves/creates a file
  useEffect(() => {
    window.addEventListener("desk-fs-changed", loadNodes);
    return () => window.removeEventListener("desk-fs-changed", loadNodes);
  }, [loadNodes]);
  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);
  useEffect(() => {
    const h = (e) => setWbOpen(e.detail?.open ? { title: e.detail.title || "Document" } : null);
    window.addEventListener("desk-wb", h);
    return () => window.removeEventListener("desk-wb", h);
  }, []);

  const openApp = (app, props = null) => {
    setWins((prev) => {
      const ex = prev.find((w) => w.app === app);
      if (ex) {
        return prev.map((w) => (w.app === app
          ? { ...w, min: false, z: nextZ(), ...(props ? { props, pk: ++pkRef.current } : {}) }
          : w));
      }
      const n = prev.length;
      const small = window.innerWidth < 640; // phones: hug the left edge so windows don't hang off-screen
      let open = prev;
      const running = prev.filter((w) => ARTCRAFT_APPS[w.app]);
      if (ARTCRAFT_APPS[app] && running.length >= MAX_ARTCRAFT_WINDOWS) {
        // Each ArtCraft program uses the graphics card; too many at once makes Chrome switch graphics off.
        open = prev.filter((w) => w !== running[0]);
        setTimeout(() => toast.info(`Closed ${ARTCRAFT_APPS[running[0].app].title} — up to ${MAX_ARTCRAFT_WINDOWS} ArtCraft programs can run at once.`, { id: "artcraft-limit" }), 0);
      }
      return [...open, {
        app,
        x: small ? 6 : 60 + n * 40,
        y: small ? 14 + n * 24 : 30 + n * 30,
        z: nextZ(), min: false, props, pk: ++pkRef.current,
      }];
    });
  };
  const closeApp = (app) => {
    setWins((prev) => prev.filter((w) => w.app !== app));
    if (app === "files") loadNodes();
  };
  const minApp = (app) => setWins((prev) => prev.map((w) => (w.app === app ? { ...w, min: true } : w)));
  const focusApp = (app) => setWins((prev) => prev.map((w) => (w.app === app ? { ...w, z: nextZ(), min: false } : w)));
  const maxApp = (app) => setWins((prev) => prev.map((w) => (w.app === app ? { ...w, maxed: !w.maxed, z: nextZ(), min: false } : w)));

  // Apps (e.g. File Manager) ask the desktop to open another app.
  useEffect(() => {
    const h = (e) => openApp(e.detail.app, e.detail.props || null);
    window.addEventListener("desk-open-app", h);
    return () => window.removeEventListener("desk-open-app", h);
  }, []);

  const startDrag = (e, app) => {
    const win = wins.find((w) => w.app === app);
    if (win?.maxed) { focusApp(app); return; }
    dragRef.current = { app, sx: e.clientX, sy: e.clientY, ox: win.x, oy: win.y };
    focusApp(app);
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      setWins((prev) => prev.map((w) => (w.app === d.app
        ? { ...w, x: Math.max(0, d.ox + ev.clientX - d.sx), y: Math.max(0, d.oy + ev.clientY - d.sy) }
        : w)));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // ---- desktop file operations (like a real desktop) ----
  const openNode = (n) => {
    if (n.type === "folder") openApp("files", { initialParent: n.id });
    else if (openWithDefault(n)) return;
    else if (n.content_type === "application/pdf") openApp("pdfcraft", { file: { id: n.id, name: n.name } });
    else if (/\.(cdoc|docx)$/i.test(n.name)) openApp("word", { file: { id: n.id, name: n.name } });
    else if (/\.(csheet|xlsx|xls)$/i.test(n.name)) openApp("sheets", { file: { id: n.id, name: n.name } });
    else openApp("files", { initialPreviewId: n.id });
  };
  const downloadNode = async (node) => {
    setMenu(null);
    try {
      const response = await api.get(`/desktop/fs/${node.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = node.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    }
  };
  const copyNodes = (list, cut = false) => {
    setMenu(null);
    if (!list.length) return;
    clipSet(list, cut);
    toast.success(list.length === 1
      ? `${cut ? "Cut" : "Copied"} "${list[0].name}" — paste it on the desktop or in a folder.`
      : `${cut ? "Cut" : "Copied"} ${list.length} items — paste them on the desktop or in a folder.`);
  };
  const copyNode = (n, cut = false) => copyNodes([n], cut);
  const copySelected = (cut = false) => copyNodes(nodes.filter((n) => selIds.has(n.id)), cut);
  const pasteDesktop = async (targetParent = DESKTOP_ROOT) => {
    setMenu(null);
    const clip = [...fsClipboard.items];
    if (!clip.length) return toast.error("Nothing to paste — copy or cut something first.");
    const wasCut = fsClipboard.cut;
    let ok = 0;
    for (const c of clip) {
      try {
        if (wasCut) await api.post(`/desktop/fs/${c.id}/move`, { parent_id: targetParent });
        else await api.post(`/desktop/fs/${c.id}/copy`, { parent_id: targetParent });
        ok += 1;
      } catch (e) { toast.error(`${c.name}: ${formatErr(e.response?.data?.detail) || e.message}`); }
    }
    if (wasCut) clipClear();
    if (ok > 1) toast.success(`Pasted ${ok} items.`);
    loadNodes();
    window.dispatchEvent(new CustomEvent("desk-fs-changed"));
  };
  const newFolder = async () => {
    setMenu(null);
    const name = window.prompt("New folder name:", "New folder");
    if (!name || !name.trim()) return;
    try { await api.post("/desktop/fs/folder", { name: name.trim(), parent_id: DESKTOP_ROOT }); loadNodes(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  const renameNode = async (n) => {
    setMenu(null);
    const name = window.prompt("Rename:", n.name);
    if (!name || !name.trim() || name === n.name) return;
    try { await api.patch(`/desktop/fs/${n.id}`, { name: name.trim() }); loadNodes(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  const deleteNode = async (n) => {
    setMenu(null);
    if (!window.confirm(`Move "${n.name}"${n.type === "folder" ? " and everything inside" : ""} to the Recycle Bin?`)) return;
    try {
      await api.delete(`/desktop/fs/${n.id}`);
      toast.success(`"${n.name}" moved to the Recycle Bin.`);
      setSelIds(new Set());
      loadNodes();
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  const deleteMany = async () => {
    setMenu(null);
    const list = nodes.filter((n) => selIds.has(n.id));
    if (!list.length) return;
    if (list.length === 1) return deleteNode(list[0]);
    if (!window.confirm(`Move ${list.length} items to the Recycle Bin?`)) return;
    try {
      await Promise.all(list.map((n) => api.delete(`/desktop/fs/${n.id}`)));
      toast.success(`${list.length} items moved to the Recycle Bin.`);
      setSelIds(new Set());
      loadNodes();
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const startMarquee = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("[data-node-id],button,input,textarea,[data-testid^='desktop-window-'],[data-testid='taskbar'],[data-testid='start-menu'],[data-testid^='sticky-'],[data-testid='taskbar-calendar']")) return;
    const additive = e.ctrlKey || e.metaKey;
    const base = additive ? new Set(selIds) : new Set();
    marqueeRef.current = { x0: e.clientX, y0: e.clientY, moved: false };
    setMarquee({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY });
    if (!additive) setSelIds(new Set());
    const move = (ev) => {
      const m = marqueeRef.current; if (!m) return;
      if (Math.abs(ev.clientX - m.x0) + Math.abs(ev.clientY - m.y0) > 4) m.moved = true;
      setMarquee({ x0: m.x0, y0: m.y0, x1: ev.clientX, y1: ev.clientY });
      const rx0 = Math.min(m.x0, ev.clientX), rx1 = Math.max(m.x0, ev.clientX);
      const ry0 = Math.min(m.y0, ev.clientY), ry1 = Math.max(m.y0, ev.clientY);
      const next = new Set(base);
      document.querySelectorAll("#mgmt-desktop-root [data-node-id]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.left < rx1 && r.right > rx0 && r.top < ry1 && r.bottom > ry0) next.add(el.getAttribute("data-node-id"));
      });
      setSelIds(next);
    };
    const up = () => {
      if (marqueeRef.current?.moved) { suppressClearRef.current = true; setTimeout(() => { suppressClearRef.current = false; }, 0); }
      marqueeRef.current = null;
      setMarquee(null);
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const onDesktopKey = (e) => {
    const tag = (e.target.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (wins.some((w) => !w.min)) return; // let open windows handle their own keys
    const sel = nodes.filter((n) => selIds.has(n.id));
    const single = sel.length === 1 ? sel[0] : null;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.key.toLowerCase() === "a") { e.preventDefault(); setSelIds(new Set(nodes.map((n) => n.id))); }
    else if (ctrl && e.key.toLowerCase() === "c" && sel.length) { e.preventDefault(); copyNodes(sel, false); }
    else if (ctrl && e.key.toLowerCase() === "x" && sel.length) { e.preventDefault(); copyNodes(sel, true); }
    else if (ctrl && e.key.toLowerCase() === "v") { e.preventDefault(); pasteDesktop(); }
    else if (e.key === "Delete" && sel.length) { e.preventDefault(); deleteMany(); }
    else if (e.key === "F2" && single) { e.preventDefault(); renameNode(single); }
    else if (e.key === "Enter" && single) { e.preventDefault(); openNode(single); }
  };

  // free icon placement: saved desk_x/desk_y wins, otherwise default grid slots
  const placed = useMemo(() => {
    const top = 16, left = 224, colW = 104, rowH = 96;
    const rows = Math.max(1, Math.floor((window.innerHeight - top - 80) / rowH));
    let slot = 0;
    return nodes.map((n) => {
      if (n.desk_x != null && n.desk_y != null) return { ...n, _x: n.desk_x, _y: n.desk_y };
      const col = Math.floor(slot / rows), row = slot % rows;
      slot += 1;
      return { ...n, _x: left + col * colW, _y: top + row * rowH };
    });
  }, [nodes]);

  const startIconDrag = (e, n, curX, curY) => {
    if (e.button !== 0) return;
    iconDragRef.current = { id: n.id, sx: e.clientX, sy: e.clientY, ox: curX, oy: curY, moved: false };
    const move = (ev) => {
      const d = iconDragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.sx, dy = ev.clientY - d.sy;
      if (!d.moved && Math.hypot(dx, dy) < 5) return;
      d.moved = true;
      setDragPos({ id: d.id, x: Math.max(0, d.ox + dx), y: Math.max(0, d.oy + dy) });
    };
    const up = async (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = iconDragRef.current;
      iconDragRef.current = null;
      setDragPos(null);
      if (!d || !d.moved) return;
      const x = Math.max(0, d.ox + ev.clientX - d.sx);
      const y = Math.max(0, Math.min(window.innerHeight - 140, d.oy + ev.clientY - d.sy));
      // dropped on a folder icon? move it inside
      const under = document.elementsFromPoint(ev.clientX, ev.clientY)
        .map((el) => el.closest?.("[data-node-id]"))
        .find((el) => el && el.dataset.nodeId !== d.id);
      if (under) {
        const folder = nodesRef.current.find((nn) => nn.id === under.dataset.nodeId && nn.type === "folder");
        if (folder) {
          try {
            await api.post(`/desktop/fs/${d.id}/move`, { parent_id: folder.id });
            toast.success(`Moved into "${folder.name}".`);
            loadNodes();
          } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
          return;
        }
      }
      setNodes((prev) => prev.map((nn) => (nn.id === d.id ? { ...nn, desk_x: x, desk_y: y } : nn)));
      api.patch(`/desktop/fs/${d.id}`, { desk_x: x, desk_y: y }).catch(() => {});
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const openDesktopMenuAt = (point, node = null) => {
    if (node) setSelIds((prev) => (prev.has(node.id) ? prev : new Set([node.id])));
    else setSelIds(new Set());
    setMenu({ x: Math.min(point.clientX, window.innerWidth - 210), y: Math.min(point.clientY, window.innerHeight - 360), node });
  };
  const onDesktopCtx = (e, node = null) => {
    e.preventDefault();
    e.stopPropagation();
    openDesktopMenuAt(e, node);
  };
  const fileLongPress = useLongPressMenu((point, node) => openDesktopMenuAt(point, node));

  return createPortal(
    <div
      className="fixed inset-0 z-[90] overflow-hidden select-none outline-none"
      style={{
        background: "radial-gradient(120% 90% at 20% 10%, #2563eb 0%, #1e3a8a 38%, #0f172a 100%)",
      }}
      tabIndex={0}
      onKeyDown={onDesktopKey}
      onMouseDown={startMarquee}
      onContextMenu={(e) => onDesktopCtx(e)}
      onClick={() => { if (!suppressClearRef.current) setSelIds(new Set()); }}
      id="mgmt-desktop-root"
      data-testid="mgmt-desktop"
    >
      {/* Pinned app icons — everything else lives in the Start menu */}
      <div className="absolute top-4 left-4 z-[1] flex flex-col flex-wrap content-start gap-3" style={{ height: "calc(100vh - 90px)" }}>
        {Object.entries(apps).filter(([k]) => deskPins.includes(k)).map(([k, a]) => (
          <button
            key={k}
            type="button"
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/app", k)}
            onDoubleClick={() => openApp(k)}
            onClick={(e) => { e.stopPropagation(); openApp(k); }}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); toggleDeskPin(k, a.title); }}
            title={`${a.title} — right-click to remove from desktop, drag onto the taskbar to pin it there`}
            className="flex flex-col items-center gap-1 w-20 py-2 rounded hover:bg-white/10 text-white"
            data-testid={`desktop-icon-${k}`}
          >
            <a.icon className={`w-9 h-9 drop-shadow ${a.color}`} strokeWidth={1.5} />
            <span className="text-[11px] font-medium drop-shadow">{a.title}</span>
          </button>
        ))}
      </div>

      {/* Root files & folders as freely movable desktop icons */}
      {placed.map((n) => {
        const { Icon, color } = fileIcon(n);
        const dragging = dragPos?.id === n.id;
        const x = dragging ? dragPos.x : n._x;
        const y = dragging ? dragPos.y : n._y;
        return (
          <button
            key={n.id}
            type="button"
            data-node-id={n.id}
            onClick={(e) => {
              if (fileLongPress.consumeClick(e)) return;
              e.stopPropagation();
              if (e.ctrlKey || e.metaKey) setSelIds((prev) => { const s = new Set(prev); if (s.has(n.id)) s.delete(n.id); else s.add(n.id); return s; });
              else setSelIds(new Set([n.id]));
            }}
            onDoubleClick={() => openNode(n)}
            onContextMenu={(e) => onDesktopCtx(e, n)}
            onPointerDown={(e) => { fileLongPress.start(e, n); startIconDrag(e, n, x, y); }}
            className={`absolute z-[2] flex flex-col items-center gap-1 w-24 py-2 px-1 rounded text-white ${selIds.has(n.id) ? "bg-white/25" : "hover:bg-white/10"} ${fsClipboard.cut && clipHas(n.id) ? "opacity-50" : ""} ${dragging ? "opacity-80" : ""}`}
            style={{ left: x, top: y, pointerEvents: dragging ? "none" : "auto", touchAction: "none", WebkitTouchCallout: "none" }}
            title={`${n.name} — double-click to open, drag to move`}
            data-testid={`desktop-node-${n.id}`}
          >
            <span className="relative">
              <Icon className={`w-9 h-9 drop-shadow ${n.shared ? "text-sky-300" : (n.type === "folder" ? "text-yellow-300" : color)}`} strokeWidth={1.3} />
              {n.shared && <Users className="absolute -bottom-1 -right-1 w-4 h-4 text-white bg-sky-600 rounded-full p-0.5" strokeWidth={2.2} />}
            </span>
            <span className="text-[11px] font-medium drop-shadow text-center break-all leading-tight max-h-8 overflow-hidden">{n.name}</span>
          </button>
        );
      })}

      {/* Marquee selection rectangle */}
      {marquee && (
        <div
          className="fixed z-[2999] border border-sky-300 bg-sky-400/15 pointer-events-none rounded-sm"
          style={{
            left: Math.min(marquee.x0, marquee.x1),
            top: Math.min(marquee.y0, marquee.y1),
            width: Math.abs(marquee.x1 - marquee.x0),
            height: Math.abs(marquee.y1 - marquee.y0),
          }}
          data-testid="desktop-marquee"
        />
      )}

      {/* Multi-selection action bar */}
      {selIds.size > 1 && !menu && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-[2998] bg-slate-900/90 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-3" data-testid="desktop-selection-bar">
          {selIds.size} selected
          <button type="button" className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 font-semibold" onClick={(e) => { e.stopPropagation(); copySelected(false); }} data-testid="desktop-selection-copy">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button type="button" className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-semibold" onClick={(e) => { e.stopPropagation(); copySelected(true); }} data-testid="desktop-selection-cut">
            <Scissors className="w-3.5 h-3.5" /> Cut
          </button>
          <button type="button" className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 font-semibold" onClick={(e) => { e.stopPropagation(); deleteMany(); }} data-testid="desktop-selection-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}

      {/* Desktop context menu */}
      {menu && (
        <div
          className="fixed z-[3100] bg-white rounded-md shadow-2xl border border-slate-200 py-1 w-52 text-sm overflow-y-auto"
          style={{ left: menu.x, top: Math.max(8, menu.y), maxHeight: `calc(100vh - ${Math.max(8, menu.y)}px - 56px)` }}
          onClick={(e) => e.stopPropagation()}
          data-testid="desktop-ctx-menu"
        >
          {!menu.node ? (
            <>
              <CtxItem icon={FolderPlus} label="New folder" onClick={newFolder} testid="desk-ctx-new-folder" />
              <CtxItem
                icon={StickyNote}
                label="New sticky note"
                onClick={() => {
                  const { x, y } = menu;
                  setMenu(null);
                  stickyRef.current?.addAt(x, y);
                }}
                testid="desk-ctx-new-sticky"
              />
              {fsClipboard.items.length > 0 && (
                <CtxItem icon={ClipboardPaste} label={fsClipboard.items.length === 1 ? `Paste "${fsClipboard.items[0].name}"` : `Paste ${fsClipboard.items.length} items`} onClick={() => pasteDesktop()} testid="desk-ctx-paste" />
              )}
              <CtxItem icon={RefreshCw} label="Refresh" onClick={() => { setMenu(null); loadNodes(); }} testid="desk-ctx-refresh" />
            </>
          ) : selIds.size > 1 && selIds.has(menu.node.id) ? (
            <>
              <CtxItem icon={Copy} label={`Copy ${selIds.size} items`} onClick={() => copySelected(false)} testid="desk-ctx-copy-many" />
              <CtxItem icon={Scissors} label={`Cut ${selIds.size} items`} onClick={() => copySelected(true)} testid="desk-ctx-cut-many" />
              <CtxItem icon={Trash2} label={`Delete ${selIds.size} items`} danger onClick={deleteMany} testid="desk-ctx-delete-many" />
            </>
          ) : menu.node.id === "__public__" ? (
            <>
              <CtxItem icon={FolderOpen} label="Open" onClick={() => { setMenu(null); openNode(menu.node); }} testid="desk-ctx-open" />
              {fsClipboard.items.length > 0 && (
                <CtxItem icon={ClipboardPaste} label="Paste into Public" onClick={() => pasteDesktop(menu.node.id)} testid="desk-ctx-paste-public" />
              )}
            </>
          ) : (
            <>
              <CtxItem icon={FolderOpen} label="Open" onClick={() => { setMenu(null); openNode(menu.node); }} testid="desk-ctx-open" />
              {menu.node.type === "file" && (
                <>
                  <OpenWithItems node={menu.node} prefix="desk-ctx" onDone={() => setMenu(null)} onMore={setOpenWith} />
                  <CtxItem icon={Download} label="Download" onClick={() => downloadNode(menu.node)} testid="desk-ctx-download" />
                  {menu.node.has_previous && <CtxItem icon={History} label="Restore previous version" onClick={() => { const node = menu.node; setMenu(null); restorePrevious(node, loadNodes); }} testid="desk-ctx-restore-previous" />}
                  <CtxItem
                    icon={PenLine}
                    label="Fill out & sign / Send to sign"
                    onClick={() => {
                      const node = menu.node;
                      setMenu(null);
                      openApp("files", { initialParent: DESKTOP_ROOT, initialSignId: node.id });
                    }}
                    testid="desk-ctx-self-sign"
                  />
                </>
              )}
              <CtxItem icon={Copy} label="Copy" onClick={() => copyNode(menu.node, false)} testid="desk-ctx-copy" />
              <CtxItem icon={Scissors} label="Cut" onClick={() => copyNode(menu.node, true)} testid="desk-ctx-cut" />
              {menu.node.type === "folder" && fsClipboard.items.length > 0 && !clipHas(menu.node.id) && (
                <CtxItem icon={ClipboardPaste} label="Paste into folder" onClick={() => pasteDesktop(menu.node.id)} testid="desk-ctx-paste-into" />
              )}
              <CtxItem icon={Pencil} label="Rename (F2)" onClick={() => renameNode(menu.node)} testid="desk-ctx-rename" />
              <CtxItem icon={Trash2} label="Delete (Del)" danger onClick={() => deleteNode(menu.node)} testid="desk-ctx-delete" />
            </>
          )}
        </div>
      )}

      {openWith && <OpenWithDialog node={openWith} onClose={() => setOpenWith(null)} />}
      {snip && <SnipTool onClose={() => setSnip(false)} />}
      {/* Sticky notes pinned to the desktop */}
      <StickyNotes ref={stickyRef} />

      {/* Windows */}
      {wins.map((w) => {
        const a = apps[w.app];
        return (
          <div
            key={w.app}
            className={`absolute shadow-2xl border border-white/20 bg-white/95 backdrop-blur flex flex-col overflow-hidden ${w.maxed ? "rounded-none" : "rounded-lg"}`}
            style={(w.maxed || smallScreen)
              ? { left: 0, top: 0, width: "100%", height: "calc(100% - 48px)", zIndex: w.z, display: w.min ? "none" : undefined }
              : { left: w.x, top: w.y, width: a.w, maxWidth: "94%", height: a.h, maxHeight: "calc(100% - 48px)", zIndex: w.z, display: w.min ? "none" : undefined }}
            onPointerDown={() => focusApp(w.app)}
            onContextMenu={(e) => e.stopPropagation()}
            data-testid={`desktop-window-${w.app}`}
          >
            <div
              className="flex items-center justify-between pl-3 pr-1 py-1.5 bg-slate-100/90 border-b border-slate-200 cursor-move"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => startDrag(e, w.app)}
              onDoubleClick={() => maxApp(w.app)}
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <a.icon className={`w-4 h-4 ${a.color}`} /> {a.title}
              </div>
              <div className="flex" onPointerDown={(e) => e.stopPropagation()}>
                <button type="button" className="p-1.5 hover:bg-slate-200 rounded" onClick={() => minApp(w.app)} data-testid={`desktop-min-${w.app}`}>
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="p-1.5 hover:bg-slate-200 rounded" title={w.maxed ? "Restore" : "Maximize"} onClick={() => maxApp(w.app)} data-testid={`desktop-max-${w.app}`}>
                  {w.maxed ? <Copy className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                </button>
                <button type="button" className="p-1.5 hover:bg-red-500 hover:text-white rounded" onClick={() => closeApp(w.app)} data-testid={`desktop-close-${w.app}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {w.app === "files" && <FileManagerApp key={w.pk} {...(w.props || {})} />}
              {w.app === "public" && <FileManagerApp key={w.pk} initialParent="__public__" />}
              {w.app === "notepad" && <NotepadApp />}
              {w.app === "calc" && <CalculatorApp />}
              {w.app === "sign" && <SignDocsApp />}
              {ARTCRAFT_APPS[w.app] && <ArtCraftApp key={w.pk} appKey={w.app} file={w.props?.file} />}
              {w.app === "money" && <MoneyCalcApp />}
              {w.app === "word" && <DocsApp key={w.pk} {...(w.props || {})} />}
              {w.app === "sheets" && <SheetsApp key={w.pk} {...(w.props || {})} />}
              {w.app === "recycle" && <RecycleBinApp />}
              {w.app === "convert" && <ConverterApp />}
              {w.app === "usb" && <UsbApp />}
              {w.app === "remote" && <RemoteDesktopApp />}
              {w.app === "logins" && isRealAdmin && <LoginsApp />}
              {w.app === "issues" && isOwner && <div className="h-full overflow-y-auto bg-background"><FeedbackAdmin /></div>}
              {w.app === "aiusage" && isOwner && <AiUsageApp />}
            </div>
          </div>
        );
      })}

      {/* Start menu */}
      {startOpen && (
        <>
          <div
            className="absolute inset-x-0 top-0 bottom-12 z-[3001]"
            onClick={() => { setStartOpen(false); setStartQuery(""); }}
            onDragOver={(e) => { if (e.dataTransfer.types.includes("text/app")) e.preventDefault(); }}
            onDrop={(e) => {
              const k = e.dataTransfer.getData("text/app");
              if (k && apps[k]) {
                e.preventDefault();
                pinDesk(k, apps[k].title);
                setStartOpen(false);
                setStartQuery("");
              }
            }}
          />
          <div
            className="absolute bottom-14 left-2 z-[3002] w-[340px] rounded-lg bg-slate-900/95 backdrop-blur-md border border-white/10 shadow-2xl p-3"
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onKeyDown={(e) => { if (e.key === "Escape") { setStartOpen(false); setStartQuery(""); } }}
            data-testid="start-menu"
          >
            <input
              autoFocus
              value={startQuery}
              onChange={(e) => setStartQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setStartOpen(false); setStartQuery(""); }
                if (e.key === "Enter") {
                  const first = Object.entries(apps).find(([, a]) => a.title.toLowerCase().includes(startQuery.trim().toLowerCase()));
                  if (first) { openApp(first[0]); setStartOpen(false); setStartQuery(""); }
                }
              }}
              placeholder="Search programs…"
              className="w-full bg-white/10 text-white text-sm rounded px-3 py-2 outline-none placeholder:text-white/40 border border-white/10 focus:border-sky-400"
              data-testid="start-search"
            />
            <div className="mt-3 grid grid-cols-3 gap-1 max-h-80 overflow-auto">
              {Object.entries(apps)
                .filter(([, a]) => a.title.toLowerCase().includes(startQuery.trim().toLowerCase()))
                .map(([k, a]) => (
                  <button
                    key={k}
                    type="button"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/app", k)}
                    onClick={() => { openApp(k); setStartOpen(false); setStartQuery(""); }}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    title={`${a.title} — drag onto the desktop or the taskbar to pin it there`}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded hover:bg-white/10 text-white"
                    data-testid={`start-app-${k}`}
                  >
                    <a.icon className={`w-7 h-7 ${a.color}`} strokeWidth={1.5} />
                    <span className="text-[11px] text-center leading-tight">{a.title}</span>
                  </button>
                ))}
              {Object.entries(apps).every(([, a]) => !a.title.toLowerCase().includes(startQuery.trim().toLowerCase())) && (
                <p className="col-span-3 text-xs text-white/50 text-center py-4" data-testid="start-no-match">No programs match &quot;{startQuery}&quot;</p>
              )}
            </div>
            <p className="mt-2.5 text-[10px] text-white/40 leading-snug px-0.5">
              Drag a program onto the desktop or the taskbar to pin it · right-click a pinned icon to remove it.
            </p>
          </div>
        </>
      )}

      {/* Taskbar */}
      <ArtCraftUpdateNotice />
      <div
        className="absolute bottom-0 inset-x-0 h-12 bg-slate-900/70 backdrop-blur-md border-t border-white/10 flex items-center justify-between px-3 z-[3000]"
        onDragOver={(e) => { if (e.dataTransfer.types.includes("text/app")) e.preventDefault(); }}
        onDrop={(e) => {
          const k = e.dataTransfer.getData("text/app");
          if (k && apps[k]) { e.preventDefault(); pinTask(k, apps[k].title); }
        }}
        data-testid="taskbar"
      >
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => { setStartOpen((v) => !v); setStartQuery(""); }}
            className={`grid grid-cols-2 gap-[2px] p-2 rounded hover:bg-white/15 ${startOpen ? "bg-white/15" : ""}`}
            title="Start — all programs"
            data-testid="start-button"
          >
            {[0, 1, 2, 3].map((i) => <span key={i} className="w-2 h-2 bg-sky-400 rounded-[1px]" />)}
          </button>
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white text-xs font-semibold"
              title="Back to Management portal"
              data-testid="taskbar-exit"
            >
              <LogOut className="w-4 h-4" /> {deskUser ? "Sign out" : "Back to portal"}
            </button>
          )}
          <button type="button" onClick={() => (onWindows() ? openWindowsSnip(() => setSnip(true)) : setSnip(true))}
            title={onWindows() ? "Microsoft Snipping Tool (Ctrl+Shift+S: desktop text snip)" : "Snipping Tool — copy words from anywhere (Ctrl+Shift+S)"}
            className="p-2 rounded hover:bg-white/15 text-sky-300" data-testid="taskbar-snip">
            <ScanText className="w-5 h-5" />
          </button>
          {[...taskPins.filter((k) => apps[k]), ...Object.keys(apps).filter((k) => !taskPins.includes(k) && wins.some((w) => w.app === k))].map((k) => {
            const a = apps[k];
            const open = wins.find((w) => w.app === k);
            const pinned = taskPins.includes(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => (open ? (open.min ? focusApp(k) : minApp(k)) : openApp(k))}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (pinned) unpinTask(k, a.title);
                }}
                className={`p-2 rounded hover:bg-white/15 ${open ? "bg-white/10 border-b-2 border-sky-400" : ""}`}
                title={pinned ? `${a.title} — right-click to unpin from the taskbar` : a.title}
                data-testid={`taskbar-${k}`}
              >
                <a.icon className={`w-5 h-5 ${a.color}`} />
              </button>
            );
          })}
          {wbOpen && (
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("desk-wb-focus"))}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/10 border-b-2 border-emerald-400 hover:bg-white/20 text-white text-xs font-semibold max-w-[220px]"
              title={wbOpen.title}
              data-testid="taskbar-signdoc"
            >
              <PenLine className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="truncate">{wbOpen.title}</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {deskUser && (
            <InstallAppButton
              manifest="/manifest-desktop.json"
              appleIcon="/desk-apple-touch.png"
              appName="the Management USA NJ desktop"
              allowDesktop
              className="h-8 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            />
          )}
          {deskUser && (
            <span className="text-xs font-semibold text-white/80 px-2" data-testid="taskbar-business-name">
              {deskUser.business_name}
            </span>
          )}
          <Clock />
        </div>
      </div>
    </div>,
    document.body
  );
}

function CtxItem({ icon: Icon, label, onClick, danger = false, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-100 ${danger ? "text-red-600" : "text-slate-700"}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
